import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  Container,
  Form,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import logoLight from "../../assets/logo/shivlogo.png";
import ParticlesAuth from "./particlesAuth";
import withRouter from "../../components/common/withRouter";
import {
  emailRegex,
  InputPlaceHolder,
  passwordRegex,
  RequiredField,
  validationMessages,
} from "../../utils/helpers/validationMasseges";
import { ResponseStatusEnum } from "../../utils/helpers/enums";
import { toast } from "react-toastify";
import {
  updatePassword,
  verifyEmail,
  verifyOtp,
} from "../../api/forgetPasswordApi";
import { AuthLabels } from "../../common/constant/Authentication";
import {
  checkStatusCodeSuccess,
  CommonLabel,
  errorHandler,
} from "../../utils/helpers/common";
import BaseInput from "../../components/BaseComponents/BaseInput";
import BaseButton from "../../components/BaseComponents/BaseButton";
import Loader from "../../components/common/loader";

const ForgetPasswordPage = (props) => {
  document.title = AuthLabels.ForgotPassword;
  const [isLoading, setIsLoading] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showpasswordsfield, setShowPasswordField] = useState(false);
  const [newPasswordShow, setNewPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [buttonLabel, setbuttonLabel] = useState(AuthLabels.SendOTP);
  const navigate = useNavigate();
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(validationMessages.required(AuthLabels.Email))
        .matches(emailRegex, validationMessages.format(AuthLabels.Email)),
      otp: Yup.string().when([], {
        is: () => showOtp,
        then: Yup.string()
          .required(validationMessages.required(AuthLabels.OTP))
          .length(4, validationMessages.characterLength(AuthLabels.OTP, 4)),
        otherwise: Yup.string().notRequired(),
      }),
      newPassword: Yup.string().when([], {
        is: () => showpasswordsfield,
        then: Yup.string()
          .min(8, validationMessages.passwordLength(AuthLabels.NewPassword, 8))
          .matches(
            passwordRegex,
            validationMessages.passwordComplexity(AuthLabels.NewPassword)
          )
          .required(validationMessages.required(AuthLabels.NewPassword)),
        otherwise: Yup.string().notRequired(),
      }),
      confirmPassword: Yup.string().when([], {
        is: () => showpasswordsfield,
        then: Yup.string()
          .oneOf(
            [Yup.ref("newPassword")],
            validationMessages.passwordsMatch(
              AuthLabels.NewPassword,
              AuthLabels.ConfirmPassword
            )
          )
          .required(validationMessages.required(AuthLabels.ConfirmPassword)),
        otherwise: Yup.string().notRequired(),
      }),
    }),
    onSubmit: (values) => {
      if (values.email !== "" && !showOtp && showpasswordsfield === false) {
        handleVerifyEmail();
      }
      if (values.otp !== "" && showOtp) {
        const payload = {
          email: values.email,
          otp: values.otp,
        };
        setIsLoading(true);
        verifyOtp(payload)
          .then((response) => {
            const responseBody = response;
            if (checkStatusCodeSuccess(responseBody?.statusCode)) {
              setShowPasswordField(true);
              setShowOtp(false);
              setbuttonLabel(CommonLabel.Submit);
              toast.success(responseBody?.message);
            } else {
              toast.error(responseBody?.message);
            }
          })
          .catch((err) => {
            errorHandler(err);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }

      if (values.newPassword !== "" && setShowPasswordField && !showOtp) {
        const payload1 = {
          email: values.email,
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        };
        setIsLoading(true);
        updatePassword(payload1)
          .then((response) => {
            const responseBody = response;
            if (checkStatusCodeSuccess(responseBody?.statusCode)) {
              toast.success(responseBody?.message);
              navigate("/login");
            } else {
              toast.error(responseBody?.message);
            }
          })
          .catch((err) => {
            errorHandler(err);
            return err;
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
  });

  const handleVerifyEmail = (isResend=false) => {
    isResend ? setIsOTPLoading(true) : setIsLoading(true);
    const verifyEmailpayload = {
      email: validation.values.email,
    };
    verifyEmail(verifyEmailpayload)
    .then((response) => {
      const responseBody = response;
      if (checkStatusCodeSuccess(responseBody?.statusCode)) {
        setShowOtp(true);
        setbuttonLabel(AuthLabels.VerifyOTP);
        toast.success(responseBody?.message);
      } else {
        toast.error(responseBody?.message);
      }
    })
    .catch((err) => {
      errorHandler(err);
    })
    .finally(() => {
      isResend ? setIsOTPLoading(false) : setIsLoading(false);
    });
  }

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-2 text-white-50">
                <div>
                  <Link to="/" className="d-inline-block auth-logo">
                    <img src={logoLight} alt="" height="30" />
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            {isOTPLoading && <Loader />}
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">
                      {AuthLabels.ForgotPassword}?
                    </h5>

                    <lord-icon
                      src="https://cdn.lordicon.com/rhvddzym.json"
                      trigger="loop"
                      colors="primary:#0ab39c"
                      className="avatar-xl"
                      style={{ width: "120px", height: "120px" }}
                    ></lord-icon>
                  </div>

                  <Alert
                    className="alert-borderless alert-warning text-center mb-2 mx-2"
                    role="alert"
                  >
                    Enter your email and OTP will be sent to you!
                  </Alert>
                  <div className="p-2">
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-2">
                        <BaseInput
                          name="email"
                          type="email"
                          placeholder={InputPlaceHolder(AuthLabels.Email)}
                          onChange={validation.handleChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.email}
                          touched={validation.touched.email}
                          error={validation.errors.email}
                          label={AuthLabels.Email}
                          disabled={showOtp || showpasswordsfield}
                          required={!showOtp && !showpasswordsfield}
                        />
                      </div>
                      {showOtp && (
                        <div className="mb-2">
                          <BaseInput
                            name="otp"
                            type="number"
                            placeholder={InputPlaceHolder(AuthLabels.OTP)}
                            onChange={validation.handleChange}
                            handleBlur={validation.handleBlur}
                            value={validation.values.otp}
                            touched={validation.touched.otp}
                            error={validation.errors.otp}
                            label={AuthLabels.OTP}
                            required
                          />
                        </div>
                      )}

                      {showpasswordsfield && (
                        <>
                          <div className="mb-2">
                            <BaseInput
                              className="form-control"
                              name="newPassword"
                              type="password"
                              placeholder={InputPlaceHolder(
                                AuthLabels.NewPassword
                              )}
                              label={AuthLabels.NewPassword}
                              onChange={validation.handleChange}
                              handleBlur={validation.handleBlur}
                              value={validation.values.newPassword}
                              touched={validation.touched.newPassword}
                              error={validation.errors.newPassword}
                              required
                            />
                          </div>
                          <div className="mb-2">
                            <BaseInput
                              className="form-control"
                              name="confirmPassword"
                              type="password"
                              placeholder={InputPlaceHolder(
                                AuthLabels.ConfirmPassword
                              )}
                              label={AuthLabels.ConfirmPassword}
                              onChange={validation.handleChange}
                              handleBlur={validation.handleBlur}
                              value={validation.values.confirmPassword}
                              touched={validation.touched.confirmPassword}
                              error={validation.errors.confirmPassword}
                              required
                            />
                          </div>
                        </>
                      )}
                      {showOtp && (
                        <div className="mb-2 d-flex justify-content-end mt-3">
                          <Link
                            onClick={() => handleVerifyEmail(true)}
                            className="text-muted"
                          >
                            {AuthLabels.ResendOTP}?
                          </Link>
                        </div>
                      )}

                      <div className="text-center">
                        <BaseButton
                          color="success"
                          type="submit"
                          label={buttonLabel}
                          loader={isLoading}
                          className="w-100"
                        />
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0">
                  Wait, I remember my password...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    {" "}
                    Click here{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgetPasswordPage);
